import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TEST_IDS } from '../../consts/testIds';
import AutocompleteComponent from '../../../../components/layout/Autocomplete';
import useTranslate from '../../../../hooks/useTranslate';
import { SRT_STATES, SRT_STATES_OPTIONS } from '../../consts/states';
import DateRangePickerComponent from '../../../../components/DateRangePickerComponent';
import { useRoles } from '../../../../hooks/useRoles';
import { useOrganizationFilter } from '../../../../contexts/OrganizationFilterContext';
import useOrganizationSelector from '../../../../components/layout/OrganizationSelector/hook/useOrganizationSelector';
import { useEffect } from 'react';
import { useAppDispatch } from '../../../../hooks/reduxHooks';
import { setSelectedOrganizationId } from '../../../../redux/slices/organizationsSlice';

interface RGRLFiltersProps {
    extraFilters: any;
    handleChangeExtraFilters: (e: any) => void;
    contractsOptions: any;
    isFetchingContractsOptions: any;
    establishmentsOptions: any;
    isFetchingEstablishmentsOptions: any;
    setFilters: any;
    startDate: any;
    endDate: any;
    handleDateChange: any;
}
const RGRLFilters = (props: RGRLFiltersProps) => {
    const {
        extraFilters,
        contractsOptions,
        isFetchingContractsOptions,
        establishmentsOptions,
        isFetchingEstablishmentsOptions,
        startDate,
        endDate,
        handleDateChange,
        handleChangeExtraFilters
    } = props;
    const { t } = useTranslation('filesGenerationSRT');
    const { translate } = useTranslate(t); 

    const dispatch = useAppDispatch();
    
    const { isPerfilBackOffice } = useRoles();
    const { selectedOrganization, setSelectedOrganization } = useOrganizationFilter();
    const { organizations: organizationsOptions } = useOrganizationSelector();

    // Sincronizar el valor del contexto con el filtro local
    useEffect(() => {        
        if (selectedOrganization && extraFilters?.idOrganizacion !== selectedOrganization) {
            handleChangeExtraFilters({
                target: {
                    name: 'idOrganizacion',
                    value: selectedOrganization
                }
            });
            dispatch(setSelectedOrganizationId(selectedOrganization));
        }
    }, [selectedOrganization, extraFilters?.idOrganizacion, handleChangeExtraFilters]);

    const handleOrganizationChange = (value: any) => {
        setSelectedOrganization(value);
        handleChangeExtraFilters({
            target: {
                name: 'idOrganizacion',
                value: value || ''
            }
        });
    };

    return (
        <>
            {isPerfilBackOffice && (
                <Grid item xs={2.5}>
                    <AutocompleteComponent
                        value={selectedOrganization || extraFilters?.idOrganizacion || null}
                        options={organizationsOptions || []}
                        size="small"
                        name="idOrganizacion"
                        loading={false}
                        label={t('header.extraFilters.organization')}
                        templateLabel={(option: any) => {
                            return `${option?.nombre}`;
                        }}
                        onChange={handleOrganizationChange}
                        clearable
                    />
                </Grid>
            )}

            <Grid item xs={isPerfilBackOffice ? 2.5 : 3}>
                <AutocompleteComponent
                    label={`${translate('header.extraFilters.contract')}`}
                    size="small"
                    name="idContrato"
                    disabled={isFetchingContractsOptions}
                    loading={isFetchingContractsOptions}
                    onChange={(value: any) => {
                        handleChangeExtraFilters({
                            target: {
                                name: 'idContrato',
                                value: value
                            }
                        });
                    }}
                    templateLabel={(item: any) =>
                        `${item?.cuit || ''} ${item?.cuit && '-'} ${item?.nombre || ''}`
                    }
                    options={contractsOptions || []}
                    value={extraFilters?.idContrato || ''}
                    testID={TEST_IDS.extraFilters.rgrl.contract}
                    clearable
                />
            </Grid>
            <Grid item xs={isPerfilBackOffice ? 2.5 : 3}>
                <AutocompleteComponent
                    label={`${translate('header.extraFilters.establishment')}`}
                    size="small"
                    name="idEstablecimiento"
                    disabled={isFetchingEstablishmentsOptions}
                    loading={isFetchingEstablishmentsOptions}
                    onChange={(value: any) => {
                        handleChangeExtraFilters({
                            target: {
                                name: 'idEstablecimiento',
                                value: value
                            }
                        });
                    }}
                    templateLabel={(item: any) =>
                        `${item?.numero || ''} ${item?.numero && '-'} ${item?.nombre || ''}`
                    }
                    options={establishmentsOptions || []}
                    value={extraFilters?.idEstablecimiento || ''}
                    testID={TEST_IDS.extraFilters.rgrl.establishment}
                    clearable
                />
            </Grid>
            <Grid item xs={isPerfilBackOffice ? 2.5 : 3}>
                <AutocompleteComponent
                    label={`${translate('header.extraFilters.stateSRT')}`}
                    size="small"
                    name="idEstadoSRT"
                    disabled={false}
                    loading={false}
                    onChange={(value: any) => {
                        handleChangeExtraFilters({
                            target: {
                                name: 'idEstadoSRT',
                                value: value
                            }
                        });
                    }}
                    options={SRT_STATES_OPTIONS}
                    templateLabel={(srtState: { id: SRT_STATES; nombre: string }) => {
                        return translate(`srtStates.${srtState?.nombre}`);
                    }}
                    value={extraFilters?.idEstadoSRT || ''}
                    testID={TEST_IDS.extraFilters.rgrl.stateSRT}
                    clearable
                />
            </Grid>
            <Grid item xs={3}>
                <DateRangePickerComponent
                    startDate={startDate}
                    endDate={endDate}
                    onChange={handleDateChange}
                />
            </Grid>
        </>
    );
};

export { RGRLFilters };
